import type { TimeEvent } from "./types";

export type Sample = { t: number; value: number };

export type EventRule = { kind: "threshold"; onAbove: number } | { kind: "band"; onAbove: number; offBelow: number };

export type EventDetector = { rule: EventRule; staleAfterMs: number };

type SampleState = "on" | "off" | "ambiguous";

export function classifySample(value: number, rule: EventRule): SampleState {
    if (value > rule.onAbove) return "on";
    if (rule.kind === "threshold" || value < rule.offBelow) return "off";
    return "ambiguous";
}

const toEvent = (startMs: number, endMs: number | null, state: SampleState): TimeEvent =>
    state === "ambiguous" ? { startMs, endMs, ambiguous: true } : { startMs, endMs };

/** Samples may start before the window; that is how the state at the window start is known. */
export function detectEvents(
    samples: Sample[],
    detector: EventDetector,
    window: { startMs: number; endMs: number },
    nowMs: number,
): TimeEvent[] {
    const events: TimeEvent[] = [];
    let run: { state: SampleState; startMs: number } = { state: "off", startMs: 0 };
    samples.forEach((sample) => {
        const state = classifySample(sample.value, detector.rule);
        if (state === run.state) return;
        if (run.state !== "off") events.push(toEvent(run.startMs, sample.t, run.state));
        run = { state, startMs: sample.t };
    });

    const lastSample = samples.at(-1);
    if (lastSample && run.state !== "off") {
        const isFresh = nowMs - lastSample.t <= detector.staleAfterMs;
        events.push(toEvent(run.startMs, isFresh ? null : lastSample.t, run.state));
    }

    return events.filter(
        (event) => !((event.endMs !== null && event.endMs <= window.startMs) || event.startMs >= window.endMs),
    );
}
